'use client';

import React, { useEffect, useRef } from "react";
import Image from "next/image";
import PhotoSwipeLightbox from "photoswipe/lightbox";
import "photoswipe/style.css";
import { motion } from "framer-motion";

// Image groups
const groupedImages = [
  {
    title: "ATS Prelude",
    groupId: "founders",
    Area: "1800 sq.ft.",
    Type: "Residential",
    Location: "Dera Bassi, Punjab",
    images: [
      "/ats1.webp", "/ats2.webp", "/ats3.webp", "/ats4.webp", "/ats5.webp",
      "/ats6.webp", "/ats7.webp", "/ats8.webp", "/ats9.webp", "/ats10.webp",
      "/ats11.webp", "/ats12.webp", "/ats13.webp"
    ],
  },
  {
    title: "GLITZ Salon ",
    groupId: "clients",
    Area: "1000 sq.ft.",
    Type: "Commercial",
    Location: "Gurugram, Haryana",
    images: ["/glitz1.webp", "/glitz2.webp", "/glitz3.webp", "/glitz4.webp", "/glitz5.webp", "/glitz6.webp"],
  },
  {
    title: "La Mansion",
    groupId: "mansion",
    Area: "6000 sq.ft.",
    Type: "Residential",
    Location: "Mohali, Punjab",
    images: ["/La1.webp", "/La2.webp", "/La3.webp", "/La4.webp"],
  },
  {
    title: "La Classical Villa",
    groupId: "classicalVilla",
    Area: "9000 sq.ft.",
    Type: "Residential",
    Location: "Mohali, Punjab",
    images: ["/luxe1.webp", "/luxe2.webp", "/luxe3.webp", "/luxe4.webp", "/luxe5.webp", "/luxe6.webp"],
  },
  {
    title: "La Arcadia",
    groupId: "residence",
    Area: "1800 sq.ft.",
    Type: "Residential",
    Location: "Vasant Vihar, New Delhi",
    images: ["/residence1.webp", "/residence3.webp", "/residence4.webp", "/residence5.webp"],
  },
  {
    title: "Satyan jewels",
    groupId: "satyan",
    Area: "200 sq.ft.",
    Type: "Commercial",
    Location: "Mohali, Punjab",
    images: ["/satyan1.webp", "/satyan2.webp", "/satyan3.webp", "/satyan4.webp", "/satyan5.webp"],
  },
];

const GalleryGrid = () => {
  const galleryRefs = useRef({});

  useEffect(() => {
    groupedImages.forEach(({ groupId }) => {
      const lightbox = new PhotoSwipeLightbox({
        gallery: `#${groupId}-gallery`,
        children: "a",
        pswpModule: () => import("photoswipe"),
      });

      lightbox.init();
      galleryRefs.current[groupId] = lightbox;
    });

  

    return () => {
      Object.values(galleryRefs.current).forEach((lightbox) => lightbox.destroy());
    };
  }, []);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 bg-primaryColor">
      <motion.h2
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="text-4xl sm:text-5xl md:text-5xl lg:text-6xl font-serif text-center text-accent mb-12 font-bold"
      >
        Spaces That Tell Stories
      </motion.h2>
      
      <div className="space-y-16">
        {groupedImages.map(({ title, groupId, images, Type, Area, Location }) => (
          <motion.div
            key={groupId}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h3 className="text-xl sm:text-4xl font-serif font-semibold text-[#733838] mb-1">
              {title}
            </h3>

            {/* Type | Area | Location - one line */}
            {(Type || Area || Location) && (
              <p className="text-sm sm:text-lg font-medium text-[#A65D5D] mb-4 sm:mb-6">
                {[Type, Area, Location].filter(Boolean).join(" | ")}
              </p>
            )}

            {/* Image Grid */}
            <div
              id={`${groupId}-gallery`}
              className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6"
            >
              {images.map((src, imageIndex) => (
                <motion.a
                  key={imageIndex}
                  href={src}
                  data-pswp-width="800"
                  data-pswp-height="600"
                  data-pswp-group={groupId}
                  className="block overflow-hidden rounded-md shadow-xl cursor-pointer bg-secondaryColor transition hover:opacity-10"
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: imageIndex * 0.05 }}
                  viewport={{ once: true }}
                >
                  <div className="relative w-full h-64">
                    <Image
                      src={src}
                      alt={`${title} Image ${imageIndex + 1}`}
                      fill
                      className="absolute top-0 left-0 object-cover"
                      priority={groupId === "founders" && imageIndex < 2}
                      fetchPriority={groupId === "founders" && imageIndex < 2 ? "high" : "low"}
                      sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, 33vw"
                    />
                  </div>
                </motion.a>
              ))}
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default GalleryGrid;
